import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import CloudIcon from '@mui/icons-material/Cloud';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import axios from 'axios';

const DublinTrunksManager = () => {
  const [trunks, setTrunks] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [createDialog, setCreateDialog] = useState(false);
  const [creatingTrunk, setCreatingTrunk] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [sipUri, setSipUri] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const { token } = useAuth();

  // Updated auth token accessor
  const getToken = () => {
    return token;
  };

  // Add linking dialog state
  const [linkDialog, setLinkDialog] = useState(false);
  const [selectedTrunk, setSelectedTrunk] = useState(null);

  useEffect(() => {
    fetchTrunks();
    fetchPhoneNumbers();
    fetchAgents();
  }, []);

  const fetchTrunks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/twilio/dublin-trunks`, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      if (response.data.success) {
        setTrunks(response.data.trunks);
      } else {
        setError(response.data.error || 'Failed to fetch trunks');
        showSnackbar('Error fetching trunks', 'error');
      }
    } catch (err) {
      console.error('Error fetching Dublin trunks:', err);
      setError('Network error or unauthorized access');
      showSnackbar('Error fetching trunks: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhoneNumbers = async () => {
    try {
      const response = await axios.get(`${API_URL}/twilio/numbers`, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      if (response.data.success) {
        setPhoneNumbers(response.data.phone_numbers);
      } else {
        console.error('Failed to fetch phone numbers:', response.data.error);
      }
    } catch (err) {
      console.error('Error fetching phone numbers:', err);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/agents`, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      if (response.data.success) {
        setAgents(response.data.agents);
      } else {
        console.error('Failed to fetch agents:', response.data.error);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenCreateDialog = () => {
    setCreateDialog(true);
    setSelectedPhoneNumber('');
    setSelectedAgent('');
    setSipUri('');
    setFriendlyName('Dublin SIP Trunk');
  };

  const handleCloseCreateDialog = () => {
    setCreateDialog(false);
  };

  const handleAgentChange = (event) => {
    const agentId = event.target.value;
    setSelectedAgent(agentId);
    
    if (agentId) {
      const agent = agents.find(a => a.id === agentId);
      if (agent) {
        // Generate a default SIP URI based on agent name
        const defaultSipUri = `sip:${agent.name.toLowerCase().replace(/\s+/g, '-')}@example.com`;
        setSipUri(defaultSipUri);
      }
    }
  };

  const handleCreateTrunk = async () => {
    if (!sipUri || !selectedAgent) {
      showSnackbar('SIP URI and Agent are required', 'error');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/twilio/create-dublin-trunk`,
        {
          agentId: selectedAgent,
          sipUri: sipUri
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`
          }
        }
      );
      
      if (response.data.success) {
        showSnackbar('Dublin trunk created successfully', 'success');
        setCreateDialog(false);
        setSipUri('');
        setSelectedAgent('');
        fetchTrunks(); // Refresh trunk list
      } else {
        showSnackbar(`Failed to create trunk: ${response.data.error}`, 'error');
      }
    } catch (err) {
      console.error('Error creating Dublin trunk:', err);
      showSnackbar('Error creating trunk: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleDeleteTrunk = async (trunkSid) => {
    if (!window.confirm(`Are you sure you want to delete trunk ${trunkSid}? This will also disconnect any associated phone numbers.`)) {
      return;
    }
    
    try {
      setLoading(true);
      showSnackbar('Deleting trunk...', 'info');
      
      const response = await axios.delete(`${API_URL}/twilio/dublin-trunks/${trunkSid}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      if (response.data.success) {
        showSnackbar('Trunk deleted successfully', 'success');
        // Refresh both trunks and phone numbers since phone numbers may have been disconnected
        await fetchTrunks();
        await fetchPhoneNumbers();
      } else {
        showSnackbar(`Failed to delete trunk: ${response.data.error}`, 'error');
      }
    } catch (err) {
      console.error('Error deleting Dublin trunk:', err);
      const errorMessage = err.response?.data?.error || err.message;
      showSnackbar(`Error deleting trunk: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLinkDialog = (trunk) => {
    setSelectedTrunk(trunk);
    setLinkDialog(true);
  };

  const handleCloseLinkDialog = () => {
    setLinkDialog(false);
    setSelectedTrunk(null);
  };

  const handleLinkPhoneToTrunk = async () => {
    if (!selectedPhoneNumber || !selectedTrunk) {
      showSnackbar('Phone number and trunk must be selected', 'error');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/twilio/numbers/${selectedPhoneNumber}/connect-dublin-trunk`,
        {
          trunkSid: selectedTrunk.sid
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`
          }
        }
      );
      
      if (response.data.success) {
        showSnackbar('Phone number linked to trunk successfully', 'success');
        setLinkDialog(false);
        setSelectedPhoneNumber('');
        fetchPhoneNumbers(); // Refresh phone numbers
      } else {
        showSnackbar(`Failed to link phone: ${response.data.error}`, 'error');
      }
    } catch (err) {
      console.error('Error linking phone to trunk:', err);
      showSnackbar('Error linking phone: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const getPhoneNumbersForTrunk = (trunkSid) => {
    return phoneNumbers.filter(phone => phone.twilio_trunk_sid === trunkSid);
  };

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.name : 'Unknown';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Dublin Trunks Manager</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTrunks}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<CloudIcon />}
            onClick={handleOpenCreateDialog}
            disabled={loading}
          >
            Create Dublin Trunk
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          What are Dublin Trunks?
        </Typography>
        <Typography variant="body1" paragraph>
          Dublin trunks are SIP trunks created in Twilio's Dublin (Ireland) region, providing improved 
          routing for European traffic. Use this manager to create, view, and manage your Dublin trunks.
        </Typography>
        <Typography variant="body2">
          A complete phone configuration requires three steps:
          <ol>
            <li>Create a Dublin trunk with a SIP URI (including the ";edge=dublin" parameter)</li>
            <li>Connect the trunk to a phone number</li>
            <li>Create a LiveKit integration for SIP calling</li>
          </ol>
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : trunks.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Dublin trunks available
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Create your first Dublin trunk to get started
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<CloudIcon />}
            onClick={handleOpenCreateDialog}
            disabled={loading}
          >
            Create Dublin Trunk
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SID</TableCell>
                <TableCell>Friendly Name</TableCell>
                <TableCell>Date Created</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trunks.map((trunk) => {
                const connectedPhones = getPhoneNumbersForTrunk(trunk.sid);
                
                return (
                  <TableRow key={trunk.sid}>
                    <TableCell>{trunk.sid}</TableCell>
                    <TableCell>{trunk.friendly_name}</TableCell>
                    <TableCell>{new Date(trunk.date_created).toLocaleString()}</TableCell>
                    <TableCell>
                      {connectedPhones.length > 0 ? (
                        connectedPhones.map(phone => (
                          <Typography key={phone.id} variant="body2">
                            {phone.phone_number} ({phone.friendly_name || 'Unnamed'})
                          </Typography>
                        ))
                      ) : (
                        'No connected numbers'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getAgentName(trunk.agent_id) || 'Not assigned'}
                        color={getAgentName(trunk.agent_id) !== 'Not assigned' ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Link to Phone Number">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenLinkDialog(trunk)}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteTrunk(trunk.sid)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dublin Trunk Dialog */}
      <Dialog
        open={createDialog}
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Dublin Trunk</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Create a new Dublin SIP trunk and optionally connect it to a phone number.
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="dublin-phone-select-label">Phone Number (Optional)</InputLabel>
              <Select
                labelId="dublin-phone-select-label"
                value={selectedPhoneNumber}
                onChange={(e) => setSelectedPhoneNumber(e.target.value)}
                label="Phone Number (Optional)"
              >
                <MenuItem value="">
                  <em>None (create trunk only)</em>
                </MenuItem>
                {phoneNumbers.map((phone) => (
                  <MenuItem key={phone.id} value={phone.id}>
                    {phone.phone_number} - {phone.country}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select a phone number to connect to this trunk, or leave empty to create a standalone trunk
              </FormHelperText>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="dublin-agent-select-label">Agent</InputLabel>
              <Select
                labelId="dublin-agent-select-label"
                value={selectedAgent}
                onChange={handleAgentChange}
                label="Agent"
                required
              >
                <MenuItem value="">
                  <em>Select an agent</em>
                </MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select the agent to associate with this trunk
              </FormHelperText>
            </FormControl>
            
            <TextField
              fullWidth
              label="SIP URI"
              value={sipUri}
              onChange={(e) => setSipUri(e.target.value)}
              margin="normal"
              required
              helperText="SIP URI must include the Dublin edge parameter (;edge=dublin)"
              error={sipUri && !sipUri.includes(';edge=dublin')}
            />
            
            <TextField
              fullWidth
              label="Friendly Name"
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              margin="normal"
              helperText="A descriptive name for this trunk"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button 
            onClick={handleCreateTrunk}
            variant="contained"
            color="primary"
            disabled={creatingTrunk || !selectedAgent || !sipUri}
          >
            {creatingTrunk ? 'Creating...' : 'Create Trunk'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Phone Number Dialog */}
      <Dialog
        open={linkDialog}
        onClose={handleCloseLinkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Phone Number to Dublin Trunk</DialogTitle>
        <DialogContent>
          {selectedTrunk && (
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Dublin Trunk: {selectedTrunk.friendly_name}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                SID: {selectedTrunk.sid}
              </Typography>
              
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel id="link-phone-select-label">Phone Number</InputLabel>
                <Select
                  labelId="link-phone-select-label"
                  value={selectedPhoneNumber}
                  onChange={(e) => setSelectedPhoneNumber(e.target.value)}
                  label="Phone Number"
                  required
                >
                  <MenuItem value="">
                    <em>Select a phone number</em>
                  </MenuItem>
                  {phoneNumbers.map((phone) => (
                    <MenuItem key={phone.id} value={phone.id}>
                      {phone.phone_number} - {phone.country}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Select a phone number to connect to this Dublin trunk
                </FormHelperText>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog}>Cancel</Button>
          <Button 
            onClick={handleLinkPhoneToTrunk}
            variant="contained"
            color="primary"
            disabled={!selectedPhoneNumber}
          >
            Link Phone Number
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DublinTrunksManager; 